import React, { useState, useContext, useEffect } from 'react';
import {
  FormSchemaFields,
  FormContext,
  FormSchema,
  FormContextProvider,
} from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { ActionButtons } from '../Shared/ActionButtons';
import { submitHandler } from '../Shared/form';
import ReactTooltip from 'react-tooltip';
import userApi from '../../utils/api/user';
import { toast } from 'react-toastify';
import { IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const getBackDatingSchema = () => {
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    disable_inspection_backdating: {
      label: 'Disable Inspection Backdating',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled inspections and inspection certifications cannot be backdated where all users will be required to use current date.',
      },
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
      },
    },
    disable_finding_backdating: {
      label: 'Disable Finding Backdating',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled users will be unable to back date findings date initiated and date completed, current date will be required for both fields.',
      },
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
      },
    },
  };
  return schema;
};

const getSchema = (
  isEnabled: boolean,
  isSameForBoth: boolean,
  selectedcertificationType: any,
  applyToAll: boolean = false,
) => {
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    certification_type: {
      label: 'Certification Type',
      controlType: UIControlType.select,
      labelKey: 'label',
      valueKey: 'value',
      options: [
        { value: 'compliance', label: 'Compliance' },
        { value: 'acknowledgement', label: 'Acknowledgement' },
        { value: 'both', label: 'Both' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: '33%',
      },
    },
    no_of_days_compliance: {
      label: 'Due In (Compliance)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    no_of_days_acknowledgment: {
      label: 'Due In (Acknowledgement)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    sameForBoth: {
      label: 'Same for both',
      controlType: UIControlType.checkbox,
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
        display: 'none',
      },
    },
    apply_to_all: {
      label: 'Apply to all',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When checked this feature will be turned on, with current setting, for all clients and projects. If left unchecked this feature will need to be turned on manually for all clients.',
      },
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
      },
    },
  };

  if (['compliance', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_compliance.validation) {
      schema.no_of_days_compliance.validation.required = true;
    }
    schema.no_of_days_compliance.disabled = false;
  }

  if (['acknowledgement', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_acknowledgment.validation) {
      schema.no_of_days_acknowledgment.validation.required = true;
    }
    schema.no_of_days_acknowledgment.disabled = false;
  }

  if (applyToAll && isEnabled && selectedcertificationType === 'both') {
    schema.sameForBoth.style!.display = 'block';
  }

  if (isSameForBoth && selectedcertificationType === 'both') {
    schema.no_of_days_compliance.label = 'Due In';
    if (schema.no_of_days_compliance.toolTipOptions) {
      schema.no_of_days_compliance.toolTipOptions.message =
        'Set the number of days after an inspection is submitted by which both the certifications must be signed';
    }
    delete schema.no_of_days_acknowledgment;
  }

  if (!isEnabled) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
    delete schema.apply_to_all;
  }

  if (!applyToAll) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
  }

  return schema;
};

const getInitialData = (data: any) => {
  return {
    is_inspection_certification_timeline_enabled:
      data?.is_inspection_certification_timeline_enabled || false,
    certification_type: data?.certification_type || '',
    no_of_days_compliance: data?.no_of_days_compliance || 0,
    no_of_days_acknowledgment: data?.no_of_days_acknowledgment || 0,
    sameForBoth:
      data?.no_of_days_compliance === data?.no_of_days_acknowledgment,
    apply_to_all: data?.apply_to_all || false,
    disable_inspection_backdating: data?.disable_inspection_backdating || false,
    disable_finding_backdating: data?.disable_finding_backdating || false,
    is_locked: data?.is_locked || false,
  };
};

const TenantInspectionDetailsUI = () => {
  const formContext = useContext(FormContext);
  const [isCollapsed, setIsCollapsed] = useState(true);

  const buildPayload = (data: any) => {
    let payload = {
      is_inspection_certification_timeline_enabled:
        data.is_inspection_certification_timeline_enabled,
      certification_type: 'compliance',
      no_of_days_compliance: 0,
      no_of_days_acknowledgment: 0,
      is_locked: data.is_locked && data.apply_to_all,
      apply_to_all: data.apply_to_all,
      disable_inspection_backdating:
        data.disable_inspection_backdating || false,
      disable_finding_backdating: data.disable_finding_backdating || false,
    };

    if (
      !data.is_inspection_certification_timeline_enabled ||
      !data.apply_to_all
    )
      return payload;

    payload.certification_type = data.certification_type;
    switch (data.certification_type) {
      case 'both':
        payload.no_of_days_compliance = data.no_of_days_compliance;
        payload.no_of_days_acknowledgment = data.sameForBoth
          ? data.no_of_days_compliance
          : data.no_of_days_acknowledgment;
        break;
      case 'compliance':
        payload.no_of_days_compliance = data.no_of_days_compliance;
        break;
      case 'acknowledgement':
        payload.no_of_days_acknowledgment = data.no_of_days_acknowledgment;
        break;
    }

    return payload;
  };

  const onSave = async (data: any) => {
    const toastId = toast('Saving Inspection Details');
    const requiredData = buildPayload(data);

    try {
      await userApi.tenantUpdateInspection(requiredData);
      toast.update(toastId, {
        render: 'Inspection saved successfully!',
        type: 'success',
      });
    } catch (error) {
      toast.update(toastId, {
        render: 'Failed to save inspection settings',
        type: 'error',
      });
      throw error;
    }
  };

  return (
    <div className='tenant-inspection-details-wrapper'>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
          marginBottom: '10px',
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3>Inspections</h3>
        </div>
        <i
          className={isCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr />
      <br />
      <div
        style={{
          display: isCollapsed ? 'none' : 'block',
        }}
      >
        <FormSchemaFields
          className='tenant-inspection-details-form findings-form'
          schema={getBackDatingSchema()}
          onChange={formContext.set}
          formData={formContext.value}
          initialFormData={getInitialData(formContext.value)}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            gap: '10px',
          }}
        >
          <FormSchemaFields
            className='tenant-inspection-details-form findings-form'
            schema={{
              is_inspection_certification_timeline_enabled: {
                label: 'Enable Inspection Certification Timeline',
                controlType: UIControlType.checkbox,
              },
            }}
            formData={formContext.value}
            onChange={formContext.set}
            initialFormData={getInitialData(formContext.value)}
          />
          {formContext.value.is_inspection_certification_timeline_enabled &&
            formContext.value.apply_to_all && (
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  formContext.set('is_locked', !formContext.value.is_locked);
                }}
                data-tip='Lock across all Divisions, Clients and Projects'
                style={{
                  marginTop: '-25px',
                }}
              >
                {formContext.value.is_locked ? (
                  <LockIcon className='col-danger' />
                ) : (
                  <LockOpenIcon className='col-success' />
                )}
              </IconButton>
            )}
        </div>
        <FormSchemaFields
          className='tenant-inspection-details-form findings-form'
          schema={getSchema(
            formContext.value.is_inspection_certification_timeline_enabled,
            formContext.value.sameForBoth,
            formContext.value.certification_type || '',
            formContext.value.apply_to_all || false,
          )}
          onChange={formContext.set}
          formData={formContext.value}
          initialFormData={getInitialData(formContext.value)}
        />
        <ActionButtons
          primary='Save'
          onClick={(e: React.SyntheticEvent<Element, Event>) =>
            submitHandler(formContext, e, onSave)
          }
        />
      </div>
    </div>
  );
};

const TenantInspectionDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await userApi.tenantInfoInspection();
        const data = response.tenantInspectionDetails;
        setInitialData(getInitialData(data));
      } catch (error) {
        console.error('Failed to fetch inspection data:', error);
        toast.error('Failed to load inspection settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading || !initialData) {
    return (
      <div className='tenant-inspection-details-wrapper'>
        <h3>Inspections</h3>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <FormContextProvider initialValue={initialData}>
      <TenantInspectionDetailsUI />
    </FormContextProvider>
  );
};

export default TenantInspectionDetails;
